import { cn } from '@invent/wl-ui-kit-next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@invent/wl-ui-kit-next/ui';
import { Check, ChevronDown, List } from 'lucide-react';
import * as React from 'react';

import {
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/design';
import type { ISavedViewSummary } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { useAdminData } from '../../components/admin-page';
import { viewRefusal } from './save-view';
import { SEEDED_MARK, visibilityLabel } from '../../lists';

/**
 * The view picker (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.3) — Conga's, made live over `GET /views?survey=&kind=list`.
 *
 * **A popover**: *Recently opened* — the views the caller has opened, newest first — then every
 * other view; a search box narrows both by name and description. **survey-api orders the list**
 * (opened first, newest first, then by name), and nothing here sorts it. Each row says its name,
 * description, visibility and whose it is: the owner's email, or **Seeded**. *Manage views…* opens
 * "Select a view": the same rows in a table, a radio per row, *Open*.
 *
 * **The list is read when the picker opens**, not once with the page: opening a view stamps its
 * `openedAt`, so a list read before is already out of order by the time anyone looks at it.
 *
 * The current view carries `*` before its name while the screen differs from it — *Changed — not
 * saved* (§3.3). Its writes are *Save view ▾*'s (`save-view.tsx`), beside this — but for *Delete* in
 * "Select a view", offered on a row the caller `mayEdit` and never on a seed (confirmed). The
 * picker's row carries no revision, so the delete reads the view first for the `ETag` it quotes.
 */

export function viewOwner(view: Pick<ISavedViewSummary, 'seed' | 'owner'>): string {
  if (view.seed !== null) return SEEDED_MARK;
  return view.owner?.email ?? 'an account since removed';
}

function describe(view: ISavedViewSummary): string {
  return [view.description, visibilityLabel(view.visibility), viewOwner(view)].filter(Boolean).join(' · ');
}

function matches(view: ISavedViewSummary, needle: string): boolean {
  return (
    needle === '' ||
    view.name.toLowerCase().includes(needle) ||
    (view.description ?? '').toLowerCase().includes(needle)
  );
}

export function ViewPicker({
  survey,
  kind = 'list',
  current,
  changed,
  onOpen,
  onDeleted
}: {
  survey: string;
  kind?: 'list' | 'report';
  /** The view on screen — its id and name — or null where the survey has none yet. */
  current: Nullable<{ id: string; name: string }>;
  changed: boolean;
  onOpen: (viewId: string) => void;
  /** A view deleted from "Select a view" — the screen's own, where it was the one on screen. */
  onDeleted: (viewId: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [managing, setManaging] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const asking = open || managing;
  const { data, error, reload } = useAdminData(
    (signal) => (asking ? listsApi.listViews(survey, kind, signal) : Promise.resolve(null)),
    [survey, kind, asking]
  );
  const needle = search.trim().toLowerCase();
  const views = (data ?? []).filter((view) => matches(view, needle));
  const recent = views.filter((view) => view.openedAt !== null);
  const others = views.filter((view) => view.openedAt === null);
  const pick = (viewId: string) => {
    setOpen(false);
    setSearch('');
    onOpen(viewId);
  };

  // A survey with no view opened and no default: the picker asks for one.
  const name = current?.name ?? 'Select a view';
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            data-view={current?.id ?? ''}
            aria-label={`View: ${name}${changed ? ', changed and not saved' : ''}`}
            className="h-8 max-w-72 gap-1.5 font-normal"
          >
            <span className="truncate">
              {changed && <span aria-hidden>*</span>}
              {name}
            </span>
            <ChevronDown className="size-4 shrink-0" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          aria-label="Views"
          className="grid max-h-[min(32rem,70dvh)] w-96 gap-2 overflow-y-auto p-3 text-[0.8125rem]"
        >
          <Input
            type="search"
            aria-label="Search the views"
            placeholder="Search views…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-8"
          />
          {error ? (
            <p role="alert" className="text-destructive">
              The views could not be read: {error}
            </p>
          ) : data === null ? (
            <p className="text-muted-foreground">Reading the views…</p>
          ) : (
            <>
              <ViewGroup title="Recently opened" views={recent} current={current?.id} onPick={pick} />
              <ViewGroup
                title={recent.length > 0 ? 'Every other view' : 'Views'}
                views={others}
                current={current?.id}
                onPick={pick}
              />
              {views.length === 0 && (
                <p className="text-muted-foreground">
                  {needle === '' ? 'No view of this survey yet.' : 'No view matches.'}
                </p>
              )}
            </>
          )}
          <Button
            type="button"
            variant="ghost"
            className="h-8 justify-start gap-1.5 border-t border-border font-normal"
            onClick={() => {
              setOpen(false);
              setManaging(true);
            }}
          >
            <List className="size-4" aria-hidden />
            Manage views…
          </Button>
        </PopoverContent>
      </Popover>
      <SelectAView
        open={managing}
        onOpenChange={setManaging}
        views={data}
        error={error}
        current={current?.id ?? null}
        onOpen={onOpen}
        onDeleted={(viewId) => {
          void reload();
          onDeleted(viewId);
        }}
      />
    </>
  );
}

function ViewGroup({
  title,
  views,
  current,
  onPick
}: {
  title: string;
  views: readonly ISavedViewSummary[];
  current: string | undefined;
  onPick: (viewId: string) => void;
}) {
  if (views.length === 0) return null;
  return (
    <div className="grid gap-0.5">
      <p className="text-xs font-medium text-[var(--veritant-royal-70)]">{title}</p>
      <ul className="grid gap-0.5">
        {views.map((view) => (
          <li key={view.id}>
            <button
              type="button"
              aria-current={view.id === current ? 'true' : undefined}
              onClick={() => onPick(view.id)}
              className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left outline-none hover:bg-[var(--accent)] focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Check
                className={cn('mt-0.5 size-4 shrink-0', view.id === current ? 'opacity-100' : 'opacity-0')}
                aria-hidden
              />
              <span className="grid min-w-0">
                <span className="font-medium">{view.name}</span>
                <span className="text-xs text-muted-foreground">{describe(view)}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Conga's "Select a View": every view as a table, one chosen by its radio and opened. */
function SelectAView({
  open,
  onOpenChange,
  views,
  error,
  current,
  onOpen,
  onDeleted
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  views: Nullable<ISavedViewSummary[]>;
  error: Nullable<string>;
  current: Nullable<string>;
  onOpen: (viewId: string) => void;
  onDeleted: (viewId: string) => void;
}) {
  const [search, setSearch] = React.useState('');
  const [chosen, setChosen] = React.useState(current);
  const [confirming, setConfirming] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<{ text: string; alert: boolean }>>(null);
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setChosen(current);
      setSaid(null);
    }
  }
  const chosenView = views?.find((view) => view.id === chosen) ?? null;
  const deletable = chosenView !== null && chosenView.mayEdit && chosenView.seed === null;

  /** The view's revision is its read's `ETag` — the row carries none — then the delete under it. */
  const remove = async (view: ISavedViewSummary) => {
    setDeleting(true);
    setSaid(null);
    try {
      const read = await listsApi.openView(view.id);
      if (read !== null) {
        if (read.etag === null) {
          setSaid({ text: 'This view came without its revision — it cannot be deleted safely.', alert: true });
          return;
        }
        await listsApi.deleteView(view.id, read.etag);
      }
      setChosen(null);
      setSaid({ text: `The view ${view.name} was deleted.`, alert: false });
      onDeleted(view.id);
    } catch (deleteError) {
      setSaid({
        text: viewRefusal(deleteError, 'the delete').said.text,
        alert: true
      });
    } finally {
      setDeleting(false);
    }
  };
  const needle = search.trim().toLowerCase();
  const shown = views?.filter((view) => matches(view, needle)) ?? null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Select a view</DialogTitle>
          <DialogDescription>Every view of this survey you may open, private and shared.</DialogDescription>
        </DialogHeader>
        <DialogBody className="gap-3">
          <Input
            type="search"
            aria-label="Search the views"
            placeholder="Search views…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <DataTable
            label="Views"
            maxHeight="20rem"
            rows={shown}
            rowKey={(view) => view.id}
            onOpen={(view) => setChosen(view.id)}
            selected={chosen}
            state={
              error
                ? { kind: 'error', content: <p role="alert">The views could not be read: {error}</p> }
                : shown === null
                  ? { kind: 'empty', content: 'Reading the views…' }
                  : { kind: 'empty', content: needle === '' ? 'No view of this survey yet.' : 'No view matches.' }
            }
            columns={[
              {
                id: 'choose',
                header: <span className="sr-only">Choose</span>,
                label: 'Choose',
                className: 'w-8 pr-0',
                cell: (view) => (
                  <input
                    type="radio"
                    name="list-view"
                    aria-label={view.name}
                    checked={chosen === view.id}
                    onChange={() => setChosen(view.id)}
                    className="accent-[var(--color-teal)]"
                  />
                )
              },
              { id: 'name', header: 'Name', cell: (view) => <span className="font-medium">{view.name}</span> },
              {
                id: 'description',
                header: 'Description',
                cell: (view) => <span className="text-muted-foreground">{view.description}</span>
              },
              { id: 'visibility', header: 'Visibility', cell: (view) => visibilityLabel(view.visibility) },
              { id: 'owner', header: 'Owner', cell: viewOwner }
            ]}
          />
        </DialogBody>
        {said && (
          <p role={said.alert ? 'alert' : 'status'} className={cn('px-6 text-sm', said.alert ? 'text-destructive' : 'text-muted-foreground')}>
            {said.text}
          </p>
        )}
        <DialogFooter>
          {chosenView !== null && (
            <Button
              type="button"
              variant="outline"
              className="mr-auto"
              disabled={!deletable || deleting}
              onClick={() => setConfirming(true)}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={chosen === null || !(views ?? []).some((view) => view.id === chosen)}
            onClick={() => {
              if (chosen === null) return;
              onOpenChange(false);
              onOpen(chosen);
            }}
          >
            Open
          </Button>
        </DialogFooter>
        {chosenView !== null && !deletable && (
          <p className="px-6 pb-4 text-xs text-muted-foreground">
            {chosenView.seed !== null
              ? 'A seeded view is never deleted.'
              : 'Only who may save over a view deletes it.'}
          </p>
        )}
      </DialogContent>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the view {chosenView?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {chosenView?.visibility === 'private'
                ? 'Nobody else sees it.'
                : 'Everyone it is shared with loses it too.'}{' '}
              Nothing undoes a delete.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirming(false);
                if (chosenView) void remove(chosenView);
              }}
            >
              Delete the view
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
